/**
 * UOM conversion — pure helpers, graph engine, and error formatting.
 */
import { formatDispensingError } from './dispensingEntity';

export const UOM_CONVERSION_RULE_TYPE = 'UOM_CONVERSION';

/** User-facing API error string (network, timeout, 4xx, 5xx, 429). */
export function formatUomConversionError(err, fallback = 'Request failed') {
  if (!err) return fallback;

  const code = err?.code;
  const message = err?.message || '';
  const status = err?.status ?? err?.response?.status;
  const detail = formatDispensingError(err, '');

  if (status === 429 || /too many requests/i.test(message) || /too many requests/i.test(detail)) {
    return detail || 'Too many requests. Please wait before trying again.';
  }
  if (code === 'ECONNABORTED' || /timeout/i.test(message)) {
    return 'Request timed out. Check your connection and try again.';
  }
  if (!err?.response && (message === 'Network Error' || code === 'ERR_NETWORK')) {
    return 'Network error. Check your connection and try again.';
  }
  if (typeof status === 'number' && status >= 500) {
    return formatDispensingError(err, 'Server error. Please try again later.');
  }
  return formatDispensingError(err, fallback);
}

/** Stable fingerprint so hooks do not re-fire on new array references. */
export function buildConversionFingerprint(conversions = []) {
  if (!conversions.length) return '';
  return conversions
    .map((c) => [
      c.id || '',
      c.from_uom_id || c.fromUomId || '',
      c.to_uom_id || c.toUomId || '',
      c.conversion_factor ?? c.factor ?? '',
    ].join(':'))
    .sort()
    .join('|');
}

export function buildUomOptionsFingerprint(uoms = []) {
  if (!uoms.length) return '';
  return uoms.map((u) => `${u.value}:${u.label}`).join('|');
}

/** Extract UOM code from select option label "Name (CODE)". */
export function uomCodeFromOptionLabel(label) {
  if (!label) return '';
  const match = String(label).match(/\(([^)]+)\)$/);
  return match ? match[1] : String(label);
}

/** Resolve UOM code from uom id using parent uoms prop [{ value, label }]. */
export function resolveUomCodeById(uomId, uoms = []) {
  if (!uomId) return '';
  const match = uoms.find((u) => String(u.value) === String(uomId));
  return uomCodeFromOptionLabel(match?.label);
}

export function parseConversionFactor(value) {
  const n = parseFloat(value);
  if (!value || Number.isNaN(n) || n <= 0) return null;
  return n;
}

/** Format factor for display — avoids float noise. */
export function formatConversionFactor(n) {
  if (n === null || n === undefined) return '?';
  const rounded = Math.round(n * 1e6) / 1e6;
  return rounded % 1 === 0
    ? String(rounded)
    : rounded.toPrecision(6).replace(/\.?0+$/, '');
}

export function isActiveUomConversionRule(rule) {
  if (!rule || rule.is_active === false) return false;
  const type = rule.rule_type;
  return type === UOM_CONVERSION_RULE_TYPE || type === 'PACKAGING';
}

/** Normalize API rule row for UI / DataGrid. */
export function normalizeUomConversionRule(rule, uoms = []) {
  if (!rule) return null;
  const factor = parseConversionFactor(rule.conversion_factor ?? rule.factor);
  return {
    ...rule,
    id: rule.id,
    fromUomId: rule.from_uom_id,
    toUomId: rule.to_uom_id,
    fromUomCode: resolveUomCodeById(rule.from_uom_id, uoms),
    toUomCode: resolveUomCodeById(rule.to_uom_id, uoms),
    factor,
    conversion_factor: rule.conversion_factor ?? (factor != null ? String(factor) : ''),
  };
}

export function normalizeUomConversionList(data, uoms = []) {
  const list = Array.isArray(data) ? data : (data?.items ?? data?.data ?? []);
  return list
    .filter(isActiveUomConversionRule)
    .map((row) => normalizeUomConversionRule(row, uoms))
    .filter(Boolean);
}

/** Collect unique UOM ids appearing in rules (excluding base). */
export function collectNonBaseUomIds(conversions = [], baseUomId) {
  const ids = new Set();
  for (const c of conversions) {
    if (c.fromUomId && String(c.fromUomId) !== String(baseUomId)) ids.add(String(c.fromUomId));
    if (c.toUomId && String(c.toUomId) !== String(baseUomId)) ids.add(String(c.toUomId));
  }
  return Array.from(ids);
}

// ── Local graph engine (fallback when /convert unavailable) ─────────────

export function buildConversionGraph(conversions) {
  const graph = new Map();

  function addEdge(from, to, factor) {
    if (!graph.has(from)) graph.set(from, []);
    graph.get(from).push({ to, factor });
  }

  for (const { fromUomCode, toUomCode, factor } of conversions) {
    if (!fromUomCode || !toUomCode || !factor) continue;
    addEdge(fromUomCode, toUomCode, factor);
    addEdge(toUomCode, fromUomCode, 1 / factor);
  }

  return graph;
}

export function resolveConversion(graph, fromCode, toCode) {
  if (fromCode === toCode) return 1;
  if (!graph.has(fromCode)) return null;

  const queue = [[fromCode, 1]];
  const visited = new Set([fromCode]);

  while (queue.length > 0) {
    const [current, factor] = queue.shift();
    for (const { to, factor: edgeFactor } of graph.get(current) || []) {
      if (visited.has(to)) continue;
      const newFactor = factor * edgeFactor;
      if (to === toCode) return newFactor;
      visited.add(to);
      queue.push([to, newFactor]);
    }
  }

  return null;
}

export function buildLocalPreviewRows(conversions, baseUomName) {
  if (!baseUomName || !conversions?.length) return [];

  const graph = buildConversionGraph(conversions);
  const allCodes = new Set();
  for (const c of conversions) {
    if (c.fromUomCode) allCodes.add(c.fromUomCode);
    if (c.toUomCode) allCodes.add(c.toUomCode);
  }
  allCodes.delete(baseUomName);

  return Array.from(allCodes).map((code) => ({
    code,
    factor: resolveConversion(graph, code, baseUomName),
    source: 'local',
  }));
}

/** Relative-tolerance compare so float noise doesn't trip false conflicts. */
function factorsConflict(a, b) {
  if (a == null || b == null) return false;
  return Math.abs(a - b) > Math.max(1e-6, Math.abs(b) * 1e-6);
}

/** Two factors are the same number, float noise ignored.
 *
 *  The GRN pack-size field is prefilled with the item master's own factor, so
 *  every submit has to answer "did the storekeeper actually change it?". A
 *  string compare answers wrong for "10" vs "10.0"; a bare === answers wrong
 *  for a value that made a round trip through a division. Either mistake sends
 *  an override on an untouched line, which turns every batch's factor source
 *  into OVERRIDE and demands a reason nobody has.
 */
export function isSameFactor(a, b) {
  if (a == null || b == null) return false;
  return !factorsConflict(a, b);
}

/** Quantize to the 6 decimals inventory_item_rules.conversion_factor holds.
 *
 *  Every factor that leaves the browser goes through here. 10 * 12 is exactly
 *  120 in float, but a composed chain is not always so kind, and the server
 *  parses whatever string arrives straight into a Decimal — so a stray
 *  119.99999999999999 would be stored, frozen onto the batch, and then divided
 *  into the line's cost, MRP and sale rate for the life of that stock.
 */
function quantizeFactor(n) {
  if (n == null || !Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 1e6) / 1e6;
}

/** The factor the server wants: this GRN line's UOM -> the item's BASE unit.
 *
 *  The storekeeper types the pack size against the unit they dispense in
 *  ("1 BOX = 10 STRIP"), because that is what the carton says. The server's
 *  conversion_factor_override means line-UOM -> base, so when the dispense unit
 *  is not the base unit those are two different numbers and the master's own
 *  STRIP -> TAB rule is what bridges them.
 *
 *  issueToBaseFactor is 1 for the ordinary item whose dispense unit IS its base
 *  unit, which is all but sixteen of them — there the typed number is sent as is.
 */
export function composePurchaseToBase(packToIssue, issueToBaseFactor) {
  const pack = parseConversionFactor(packToIssue);
  const bridge = parseConversionFactor(issueToBaseFactor);
  if (pack == null || bridge == null) return null;
  return quantizeFactor(pack * bridge);
}

/** The inverse, for prefilling the field from the item master's own rules.
 *
 *  Both arguments come from the item's conversion graph, so "1 BOX = 120 TAB"
 *  and "1 STRIP = 12 TAB" prefill the field with 10.
 */
export function defaultPackToIssue(purchaseToBaseFactor, issueToBaseFactor) {
  const purchase = parseConversionFactor(purchaseToBaseFactor);
  const bridge = parseConversionFactor(issueToBaseFactor);
  if (purchase == null || bridge == null) return null;
  return quantizeFactor(purchase / bridge);
}

export function validateNewConversionForm({ fromUomId, toUomId, factor, conversions = [], uoms = [] }) {
  if (!fromUomId) return 'Select a "From" UOM';
  if (!toUomId) return 'Select a "To" UOM';
  if (fromUomId === toUomId) return '"From" and "To" must be different';
  const parsed = parseConversionFactor(factor);
  if (parsed == null) return 'Factor must be a positive number';

  const duplicate = conversions.find(
    (c) => String(c.fromUomId) === String(fromUomId) && String(c.toUomId) === String(toUomId),
  );
  if (duplicate) {
    return `A rule for this From/To pair already exists (× ${formatConversionFactor(duplicate.factor)}). Edit or delete it instead of adding a duplicate.`;
  }

  const fromCode = resolveUomCodeById(fromUomId, uoms);
  const toCode = resolveUomCodeById(toUomId, uoms);
  if (fromCode && toCode && conversions.length) {
    const graph = buildConversionGraph(conversions);
    const existingFactor = resolveConversion(graph, fromCode, toCode);
    if (existingFactor != null && factorsConflict(existingFactor, parsed)) {
      return `This conflicts with the existing conversion path: 1 ${fromCode} already resolves to ${formatConversionFactor(existingFactor)} ${toCode} through current rules. Remove the conflicting rule first, or use ${formatConversionFactor(existingFactor)}.`;
    }
  }

  return null;
}

const FACTOR_WARN_MAX = 100000;
const FACTOR_WARN_MIN = 0.0001;

/** Non-blocking typo guard — large/small factors are usually mis-entered. */
export function getFactorSanityWarning(factor) {
  const parsed = parseConversionFactor(factor);
  if (parsed == null) return null;
  if (parsed >= FACTOR_WARN_MAX || parsed <= FACTOR_WARN_MIN) {
    const size = parsed >= FACTOR_WARN_MAX ? 'large' : 'small';
    return `A factor of ${formatConversionFactor(parsed)} looks unusually ${size} — double-check it's correct before saving.`;
  }
  return null;
}

/** Non-blocking heads-up: does the pending rule actually reach base UOM? */
export function previewConnectivityAfterAdd({
  conversions = [], uoms = [], baseUomName, fromUomId, toUomId, factor,
}) {
  const parsed = parseConversionFactor(factor);
  if (!baseUomName || !fromUomId || !toUomId || parsed == null) return null;

  const fromCode = resolveUomCodeById(fromUomId, uoms);
  const toCode = resolveUomCodeById(toUomId, uoms);
  if (!fromCode || !toCode) return null;

  const hypothetical = [...conversions, { fromUomCode: fromCode, toUomCode: toCode, factor: parsed }];
  const graph = buildConversionGraph(hypothetical);
  const fromResolved = resolveConversion(graph, fromCode, baseUomName) != null;
  const toResolved = resolveConversion(graph, toCode, baseUomName) != null;
  if (fromResolved && toResolved) return null;

  const unresolvedCode = !fromResolved ? fromCode : toCode;
  return `This rule alone won't connect ${unresolvedCode} to base UOM ${baseUomName} yet — you'll need another rule bridging ${unresolvedCode} to ${baseUomName}.`;
}

/** Warn before deleting a rule if it's the only bridge keeping other UOMs resolved. */
export function getDeleteImpactWarning({ conversions = [], ruleToDelete, baseUomName }) {
  if (!ruleToDelete || !baseUomName || !conversions.length) return null;

  const beforeGraph = buildConversionGraph(conversions);
  const remaining = conversions.filter((c) => c.id !== ruleToDelete.id);
  const afterGraph = buildConversionGraph(remaining);

  const codes = new Set();
  conversions.forEach((c) => {
    if (c.fromUomCode) codes.add(c.fromUomCode);
    if (c.toUomCode) codes.add(c.toUomCode);
  });
  codes.delete(baseUomName);

  const broken = Array.from(codes).filter((code) => {
    const before = resolveConversion(beforeGraph, code, baseUomName);
    const after = resolveConversion(afterGraph, code, baseUomName);
    return before != null && after == null;
  });

  if (!broken.length) return null;
  return `Deleting this rule will disconnect ${broken.join(', ')} from base UOM ${baseUomName}.`;
}

// ── Dispensing / issuing in a non-base UOM ──────────────────────────────
//
// Stock is counted in the item's BASE unit (tablets). A pharmacist counts in
// the item's ISSUE unit (strips). The catalogue resolves the bridge between
// them — issue_uom_factor, "how many base units one issue unit is" — and
// restates prices per issue unit alongside it.
//
// The rule these helpers exist to enforce: the front end never converts. It
// reads the factor to *label*, to *cap*, and to *refuse*; the quantity it
// submits is the number typed, paired with the issue UOM id, and stock-ledger
// does the one and only conversion. Converting here as well would debit
// twice over.

/** How many base units one issue unit is worth. Null when unresolvable. */
export function issueUomFactor(source) {
  const factor = Number(source?.issue_uom_factor);
  return Number.isFinite(factor) && factor > 0 ? factor : null;
}

/** The unit a quantity is entered in, e.g. 'STR'. Item master's; read-only wherever it is shown. */
export function issueUomCode(source) {
  return source?.issue_uom_code || source?.base_uom_code || '';
}

/**
 * How many base units one issue unit is worth FOR THIS BATCH.
 *
 * Batches of the same medicine can carry different pack sizes: the item master
 * says 1 STRIP = 12 TAB, but one delivery arrived as strips of 10 and its GRN
 * line froze that. Old and new batches sit in the ledger together and both
 * dispense correctly, so a per-row number is the only honest one.
 *
 * Falls back to the item's own factor — which is what every batch used before
 * per-batch pack sizes existed, and what batches with no GRN line (opening
 * stock, consignment, adjustment-created) still use.
 */
export function batchIssueUomFactor(batch, item) {
  const batchFactor = Number(batch?.issue_uom_factor);
  if (Number.isFinite(batchFactor) && batchFactor > 0) return batchFactor;
  return issueUomFactor(item);
}

/**
 * Whole issue units across every batch, each counted at its own pack size.
 *
 * NOT floor(total available / factor). 30 + 30 tablets at 12 to the strip is
 * 2 + 2 = 4 strips, not floor(60 / 12) = 5 — the fifth strip does not
 * physically exist, and promising it is how a dispense fails at the counter.
 *
 * Prefers the server's own total_issuable_quantity when the catalogue sends
 * one, since that is the number the ledger will actually honour.
 */
export function totalIssuableQty(batches, item) {
  const fromServer = Number(item?.total_issuable_quantity);
  if (Number.isFinite(fromServer) && fromServer >= 0) return fromServer;
  if (!Array.isArray(batches) || !batches.length) return 0;
  return batches.reduce((sum, batch) => {
    if (batch?.is_expired) return sum;
    return sum + maxIssueQty(batch?.available_quantity, batchIssueUomFactor(batch, item));
  }, 0);
}

/** The unit stock is counted in, e.g. 'TAB'. */
export function baseUomCode(source) {
  return source?.base_uom_code || '';
}

/** The UOM id a movement should be submitted with. */
export function issueUomId(source) {
  return source?.issue_uom_id || source?.uom_id || source?.base_uom_id || '';
}

/** How many whole issue units the base-unit stock covers — 599 TAB at 10 to
 *  the strip is 59 strips, not 59.9. Part-units are not dispensable. */
export function maxIssueQty(availableBase, factor) {
  if (!factor) return 0;
  return Math.floor((Number(availableBase) || 0) / factor);
}

/** An issue-unit quantity restated in base units, for display hints only —
 *  never for anything that is submitted. */
export function baseQtyOf(qty, factor) {
  // An empty input is "no quantity yet", not zero — Number('') is 0, which
  // would read back as a real answer.
  if (qty === '' || qty == null) return null;
  const q = Number(qty);
  if (!factor || !Number.isFinite(q)) return null;
  return q * factor;
}

/**
 * Why an issue-unit quantity cannot be dispensed as entered, or null when it
 * can. `availableBase` is in base units — that mismatch is the entire reason
 * this lives in one place instead of being re-derived per screen.
 *
 * `max` optionally caps for a non-stock reason (a prescribed or returnable
 * quantity, already in issue units).
 */
export function issueQtyIssue({ qty, factor, availableBase, code, max }) {
  if (!factor) return 'UOM conversion missing';
  const q = Number(qty);
  if (!Number.isFinite(q) || q <= 0) return 'Enter a quantity';
  // Half a strip is not a thing you can hand over. A factor of 1 means the
  // quantity is already in base units, where fractions (5.5 ml) are ordinary —
  // so the whole-unit rule applies only to real pack units.
  if (factor > 1 && !Number.isInteger(q)) return `Whole ${code || 'units'} only`;
  if (max != null && q > Number(max)) return `Max ${max}`;
  if (availableBase != null) {
    const cap = maxIssueQty(availableBase, factor);
    if (q > cap) return `Max ${cap}`;
  }
  return null;
}

/**
 * Availability written in the unit the quantity beside it is entered in —
 * "57 STR", not "575 TAB".
 *
 * Deliberately the same number `issueQtyIssue` caps at, so the column and the
 * "Max 57" it would produce can never disagree. It floors: 575 tablets is 57
 * whole strips and 5 loose tablets, and a loose tablet cannot be handed over
 * as part of a strip.
 *
 * Falls back to the raw base figure when there is no factor — better an
 * unconverted number with its own unit on it than a blank column.
 */
export function formatAvailableQty(availableBase, factor, issueCode, baseCode, options = {}) {
  // Number('') and Number(null) are both 0, which would print "0 STR" for a
  // column that simply has not loaded yet.
  if (availableBase === '' || availableBase == null) return '';
  const base = Number(availableBase);
  if (!Number.isFinite(base)) return '';
  if (!factor) return baseCode ? `${base} ${baseCode}` : String(base);
  const qty = maxIssueQty(base, factor);
  const label = issueCode ? `${qty} ${issueCode}` : String(qty);
  // A part-pack remainder is invisible stock, not dead stock — base-unit issue,
  // adjustments, transfers, discard and vendor return all still move it. Saying
  // so is cheaper than the "where did my 6 tablets go" ticket.
  if (options.showLoose) {
    const loose = base - (qty * factor);
    if (loose > 0) {
      const rounded = Math.round(loose * 1e6) / 1e6;
      return baseCode ? `${label} (${rounded} ${baseCode} loose)` : `${label} (${rounded} loose)`;
    }
  }
  return label;
}

/** Base units across every batch that no whole issue unit could claim.
 *
 *  Summed per batch at that batch's own pack size, because a part pack is a
 *  property of the batch it sits in. Still real, movable stock — base-unit
 *  issue, adjustments, transfers, discard and vendor return all reach it — so
 *  it is worth naming rather than quietly dropping off the screen.
 */
function looseBaseQty(batches, item) {
  if (!Array.isArray(batches) || !batches.length) return 0;
  const loose = batches.reduce((sum, batch) => {
    if (batch?.is_expired) return sum;
    const avail = Number(batch?.available_quantity) || 0;
    const factor = batchIssueUomFactor(batch, item);
    // No resolvable conversion means not one unit of it can be made up into a
    // pack, so all of it is loose.
    if (!factor) return sum + avail;
    return sum + (avail - maxIssueQty(avail, factor) * factor);
  }, 0);
  return Math.round(loose * 1e6) / 1e6;
}

/**
 * An item's whole-pack availability written in the issue unit — the twin of
 * formatAvailableQty for a total that spans batches.
 *
 * formatAvailableQty divides ONE batch by ONE factor, which is the only honest
 * arithmetic it can do. An item total has no single factor to divide by the
 * moment two batches carry different pack sizes: 50 capsules at 10 to the
 * strip plus 32 at 8 is 5 + 4 = 9 strips, while floor(82 / 10) is 8 — a whole
 * strip that physically exists, is sellable, and no screen would ever offer.
 */
export function formatIssuableQty(batches, item, options = {}) {
  const rows = Array.isArray(batches) ? batches.filter((b) => !b?.is_expired) : [];
  const base = baseUomCode(item);
  // Nothing resolves a conversion, so nothing can be stated in packs. Fall
  // back the way formatAvailableQty does — an unconverted number with its own
  // unit on it, never a "0" that reads as an empty shelf.
  if (issueUomFactor(item) == null && !rows.some((b) => batchIssueUomFactor(b, item) != null)) {
    const total = rows.length
      ? rows.reduce((sum, b) => sum + (Number(b?.available_quantity) || 0), 0)
      : Number(item?.total_available_quantity ?? item?.available_quantity) || 0;
    return base ? `${total} ${base}` : String(total);
  }
  const qty = totalIssuableQty(batches, item);
  const issueCode = issueUomCode(item);
  const label = issueCode ? `${qty} ${issueCode}` : String(qty);
  if (!options.showLoose) return label;
  const loose = looseBaseQty(batches, item);
  if (!loose) return label;
  return base ? `${label} (${loose} ${base} loose)` : `${label} (${loose} loose)`;
}

/** "= 20 TAB" hint under an issue-unit input. Empty when there is nothing to add. */
export function baseQtyHint(qty, factor, baseCode) {
  if (!factor || factor === 1 || !baseCode) return '';
  const base = baseQtyOf(qty, factor);
  if (base == null || base <= 0) return '';
  return `= ${base} ${baseCode}`;
}

/** Group unresolved preview rows by connected component so one bridging rule fixes each group together. */
export function groupUnresolvedPreviews(previews = [], conversions = [], baseUomName) {
  const unresolved = previews.filter((p) => p.factor == null);
  if (!unresolved.length) return [];

  const graph = buildConversionGraph(conversions);
  const unresolvedCodes = new Set(unresolved.map((p) => p.code));
  const visited = new Set();
  const groups = [];

  for (const row of unresolved) {
    if (visited.has(row.code)) continue;

    const queue = [row.code];
    const componentCodes = new Set([row.code]);
    visited.add(row.code);

    while (queue.length) {
      const current = queue.shift();
      for (const { to } of graph.get(current) || []) {
        if (componentCodes.has(to) || to === baseUomName) continue;
        componentCodes.add(to);
        if (unresolvedCodes.has(to)) visited.add(to);
        queue.push(to);
      }
    }

    groups.push({
      codes: Array.from(componentCodes),
      rows: unresolved.filter((u) => componentCodes.has(u.code)),
    });
  }

  return groups;
}
